import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Space, Row, Col, Select, message } from 'antd';
import { useB2cStudentDetail } from 'hooks';
import { CheckOutlined } from '@ant-design/icons'
import { CompanySelectBox } from 'components/atoms'
import { CompanyMatchModal, StudentDeleteModal } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { fetcher } from 'services';
import { makeMaskPhoneNumber } from "utils/form-utils"

export interface StudentEditModalProps extends PopupProps {
    id: number;  
    group: string;
    lectureInfo: LectureInfoDto;
}

const SeminarStudentEditModal = ({ 
    isOpen, 
    onClose, 
    id, group, lectureInfo}: StudentEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const jobOption = 
        [
            {value:'대표', label: '대표'},
            {value:'임원', label: '임원'},
            {value:'팀장', label: '팀장'},
            {value:'팀원', label: '팀원'},
        ]    
        
    const statusOption = 
        [
            {value:'confirm', label: '참석'},
            {value:'holding', label: '대기'},
            {value:'unfit', label: '부적격'},
            {value:'drop', label: '취소'},
        ]          
    
    const roleOption = 
        [
            {value:'교육담당자', label: '교육담당자'},
            {value:'인사담당자', label: '인사담당자'},
            {value:'전략담당자', label: '전략담당자'},
            {value:'영업/구매담당자', label: '영업/구매담당자'},
            {value:'기타', label: '기타'},
        ]        

  /* ======================================================
  * hooks
  * ====================================================== */
  const {studentInfo, appmInsert, appmUpdate, insertSuccessAppm, updateSuccessAppm } = useB2cStudentDetail(id, group);
  const [companySrl, setCompanySrl] = useState<number>(0);
  const [companyName, setCompanyName] = useState<string>('');  
   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input;
  const { open } = usePopupManager();  

   /* ======================================================
   * events
   * ====================================================== */
    const onDelete = (putoff: boolean) => {
        
        if (!!studentInfo) {
            open(StudentDeleteModal, {
                lectureInfo: lectureInfo,
                id: id,
                group: group,
                putoff: putoff,
                onClose: (isOk) => {
                  if (isOk) {
                    onClose?.(true);
                  };
                },
            });  
        }
    }

    const onSubmit = (e) => {
        const sData = {
            id: id,
            b2c_group_srl: lectureInfo.id,
            company: companySrl?companyName:e.company,
            company_srl: companySrl,
            name_kor: e.name_kor,
            department: e.department,
            position: e.position,
            phone: e.phone,
            email: e.email,
            job : e.job,
            role: e.role,
            status_type: e.status_type || 'confirm',
            app_memo: e.app_memo,
        }  

        if (id > 0) {
            appmUpdate(sData);
        } else {
            appmInsert(sData);
        }
    };

    const onCloseClick = () => {
        onClose?.(studentInfo?.company_srl !== companySrl);
    }
    const onClickCompanyMatch = () => {
        if (studentInfo) {
            open(CompanyMatchModal, {
                company: companyName,
                onClose: (isOk, company_srl, company_name) => {
                  if (isOk) {
                      setCompanySrl(company_srl);
                      setCompanyName(company_name);
                  };
                },
            }); 
        }
      }

  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        if (!!studentInfo) {   
            form.setFieldsValue({
                company: studentInfo.company, 
                company_srl: studentInfo.company_srl, 
                name_kor: studentInfo.name_kor, 
                department: studentInfo.department, 
                position: studentInfo.position, 
                mem_price: studentInfo.mem_price, 
                phone: makeMaskPhoneNumber(studentInfo.phone), 
                email: studentInfo.email, 
                subject: studentInfo.subject, 
                app_memo: studentInfo.app_memo, 
                status_type: studentInfo.status_type,
                job: studentInfo.job,
                role: studentInfo.role,
            });

            form.setFieldValue('mem_price', studentInfo.mem_price);

            setCompanySrl(studentInfo.company_srl);
            setCompanyName(studentInfo.company);

        }
    }, [studentInfo])

    useEffect(() => {
        if (insertSuccessAppm || updateSuccessAppm) {   
            onClose?.(true);
        }
    }, [insertSuccessAppm, updateSuccessAppm])

    return (
    <ModalWrapper open={isOpen} onCancel={onCloseClick} width={700} title={id===0?'교육생 등록':'교육생 수정'} closable>
        <Space direction='vertical' style={{width:'100%'}}>
            <h4 style={{marginBottom:'0px'}}>{lectureInfo.lecture_title}</h4>
            <label ><CheckOutlined /> 고객사</label>
            <Form
                form={form}
                name="StudentInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                {(id===0 || !!companySrl) ? (
                    <Form.Item name="company_srl" label={'고객사(기업명)'} rules={[{ required: true, message: '' }]}>
                        <CompanySelectBox required={true}  form={form} company_srl={companySrl} onSelected={(value, option) => {setCompanySrl(Number(value)); setCompanyName(option.label);}} />
                    </Form.Item>  
                ): (
                    <Form.Item name="company" label={'고객사명(기업명)'} rules={[{ required: true, message: '' }]}>      
                        <Form.Item name={'company'} style={{ width:'150px', display: 'inline-block', marginRight: '10px' }}>
                            <Input type="text" placeholder='' onChange={(e) => {                        
                                const pattern = /[a-z0-9]|[ \[\]{}()<>?|`~!@#$%^&*-_+=,.;:\"'\\]/g;
                                form.setFieldValue('company', e.target.value.replace(pattern, ''));
                            }}/>                   
                        </Form.Item> 
                        <Button type="primary" onClick={onClickCompanyMatch}>고객사 매칭</Button>
                    </Form.Item>                    
                )}
            </Form>
            <label ><CheckOutlined /> 교육생</label>
            <Form
                form={form}
                name="StudentInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                <Row>
                    <Col span={12}>
                    <Form.Item name={'name_kor'} label={'이름'} rules={[{ required: true, message: '' }]}>
                        <Input type="text" placeholder='' />                 
                    </Form.Item> 
                    </Col> 
                    <Col span={12}> 
                    <Form.Item name={'department'} label={'부서'} rules={[{ required: true, message: '' }]}>
                        <Input type="text" placeholder='' />                 
                    </Form.Item> 
                    </Col>            
                </Row>
                <Row>
                    <Col span={12}>
                    <Form.Item name={'position'} label={'직위'} rules={[{ required: true, message: '' }]}>
                        <Input type="text" placeholder='' />                 
                    </Form.Item> 
                    </Col> 
                    <Col span={12}> 
                    <Form.Item name={'job'} label={'직책'} >
                        <Select 
                            placeholder="직책 선택"
                            options={jobOption}
                        />               
                    </Form.Item>   
                    </Col>            
                </Row>
                <Row>
                    <Col span={12}>
                    <Form.Item name={'phone'} label={'핸드폰'} rules={[{ required: true, message: '' }]}>
                        <Input placeholder='핸드폰번호를 입력하세요' onChange={(e) => form.setFieldValue('phone', makeMaskPhoneNumber(e.target.value))} />                 
                    </Form.Item> 
                    </Col> 
                    <Col span={12}> 
                    <Form.Item name={'email'} label={'이메일'} rules={[{ required: true, message: '' }]}>
                        <Input type="text" placeholder='' />                 
                    </Form.Item>   
                    </Col>            
                </Row>
                <Form.Item name='role' label={'담당업무'}>
                    <Select 
                        placeholder="담당업무 선택"
                        options={roleOption}
                    />
                </Form.Item> 
                {(id>0) && (
                <>
                <Row>
                    <Col span={12}>
                    <Form.Item label={'신청경로'}>
                        <label>{studentInfo?.route}</label>
                    </Form.Item> 
                    </Col> 
                    <Col span={12}> 
                    <Form.Item label={'신청일시'}>
                        <label>{studentInfo?.reg_dttm}</label>
                    </Form.Item> 
                    </Col>            
                </Row>
                <Form.Item name='status_type' label={'상태'}>
                    <Select 
                        placeholder="상태 선택"
                        options={statusOption}
                    />
                </Form.Item>
                </>                
                )}                   
                <Form.Item name='app_memo' label={'메모'}>
                    <TextArea rows={2} placeholder='메모를 입력하세요'/>   
                </Form.Item>      
                <ButtonGroup className={'buttonsWrapper'}>
                    <div>
                        <Button onClick={onCloseClick} style={{marginRight: "5px"}}>닫기</Button>
                        {(id>0) && (<Button onClick={()=>onDelete(false)} style={{marginRight: "5px", width:'80px'}}>일정취소</Button>)}                        
                    </div>
                    <div>
                        <Button htmlType="submit"  type="primary">{id===0?'등록':'수정'}</Button>
                    </div>                      
                </ButtonGroup>                      
            </Form> 
        </Space>        
    </ModalWrapper>  
    )    
};

export default React.memo(SeminarStudentEditModal);    